import {
  PROVIDER_SEND_TURN_MAX_INLINE_FILE_BYTES,
  type DesktopPickedAttachment,
  type UploadChatAttachment,
  type UploadChatFolderAttachmentEntry,
} from "@afar-research/contracts";

import { formatAttachmentSize } from "@afar-research/client-runtime/state/attachments";

import { readFileAsDataUrl } from "../components/ChatView.logic";
import type { ComposerFileAttachment } from "../composerDraftStore";

export type ComposerPathAttachment =
  | {
      readonly id: string;
      readonly source: "desktop";
      readonly picked: DesktopPickedAttachment;
      readonly type: "file" | "folder";
      readonly name: string;
    }
  | {
      readonly id: string;
      readonly source: "host-native";
      readonly type: "file" | "folder";
      readonly name: string;
      readonly path: string;
    }
  | {
      readonly id: string;
      readonly source: "browser-file";
      readonly type: "file";
      readonly name: string;
      readonly file: File;
    }
  | {
      readonly id: string;
      readonly source: "browser-folder";
      readonly type: "folder";
      readonly name: string;
      readonly entries: ReadonlyArray<{ readonly relativePath: string; readonly file: File }>;
      readonly directoryHandle?: FileSystemDirectoryHandle;
    };

export function desktopComposerAttachment(
  id: string,
  picked: DesktopPickedAttachment,
): ComposerPathAttachment {
  return { id, source: "desktop", picked, type: picked.type, name: picked.name };
}

export function hostNativeComposerAttachment(
  id: string,
  input: {
    readonly type: "file" | "folder";
    readonly name: string;
    readonly path: string;
  },
): ComposerPathAttachment {
  return {
    id,
    source: "host-native",
    type: input.type,
    name: input.name,
    path: input.path,
  };
}

export function composerAttachmentHostPath(attachment: ComposerPathAttachment): string | undefined {
  if (attachment.source === "desktop") return attachment.picked.path;
  if (attachment.source === "host-native") return attachment.path;
  return undefined;
}

/** Codex-style chip subtitle: file extension or "Folder". */
export function composerAttachmentKindLabel(attachment: ComposerPathAttachment): string {
  if (attachment.type === "folder") return "Folder";
  const dot = attachment.name.lastIndexOf(".");
  if (dot <= 0 || dot === attachment.name.length - 1) return "File";
  return attachment.name.slice(dot + 1).toUpperCase();
}

type ShowDirectoryPicker = (options?: {
  readonly mode?: "read" | "readwrite";
}) => Promise<FileSystemDirectoryHandle>;

function getShowDirectoryPicker(): ShowDirectoryPicker | undefined {
  const candidate = (
    globalThis as typeof globalThis & {
      showDirectoryPicker?: ShowDirectoryPicker;
    }
  ).showDirectoryPicker;
  return typeof candidate === "function" ? candidate : undefined;
}

/** Whether this browser can pick a directory without the "Upload N files" prompt. */
export function browserFolderPickSupported(): boolean {
  return getShowDirectoryPicker() !== undefined;
}

/**
 * Attach one folder via the File System Access API (no Chrome "Upload N files" prompt).
 * Returns null when unsupported or when the user cancels.
 */
export async function pickBrowserFolderAttachment(
  makeId: () => string,
): Promise<ComposerPathAttachment | null> {
  const showDirectoryPicker = getShowDirectoryPicker();
  if (!showDirectoryPicker) return null;
  try {
    const directoryHandle = await showDirectoryPicker({ mode: "read" });
    return {
      id: makeId(),
      source: "browser-folder",
      type: "folder",
      name: directoryHandle.name,
      entries: [],
      directoryHandle,
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return null;
    throw error;
  }
}

async function collectDirectoryHandleFiles(
  directory: FileSystemDirectoryHandle,
  prefix = "",
): Promise<Array<{ readonly relativePath: string; readonly file: File }>> {
  const result: Array<{ readonly relativePath: string; readonly file: File }> = [];
  for await (const [name, handle] of directory.entries()) {
    const relativePath = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === "file") {
      result.push({ relativePath, file: await handle.getFile() });
    } else if (handle.kind === "directory") {
      result.push(...(await collectDirectoryHandleFiles(handle, relativePath)));
    }
  }
  return result;
}

async function resolveBrowserFolderEntries(
  attachment: Extract<ComposerPathAttachment, { readonly source: "browser-folder" }>,
): Promise<ReadonlyArray<{ readonly relativePath: string; readonly file: File }>> {
  if (attachment.entries.length > 0) return attachment.entries;
  if (attachment.directoryHandle) {
    return collectDirectoryHandleFiles(attachment.directoryHandle);
  }
  return [];
}

/**
 * Whether a file's bytes go ahead through the signed upload. A send that may
 * carry many files uploads every non-empty one ahead, so the turn itself
 * stays small on the WebSocket; otherwise only files over the inline limit do.
 */
function travelsAhead(file: File, uploadAhead: boolean): boolean {
  return uploadAhead ? file.size > 0 : file.size > PROVIDER_SEND_TURN_MAX_INLINE_FILE_BYTES;
}

/** Sends one file's bytes through the signed attachment upload; resolves to the pending id. */
export type ComposerAttachmentFileUploader = (file: File) => Promise<string>;

async function uploadedBrowserAttachment(
  attachment: Exclude<
    ComposerPathAttachment,
    { readonly source: "desktop" } | { readonly source: "host-native" }
  >,
  uploadFile: ComposerAttachmentFileUploader,
  uploadAhead: boolean,
): Promise<UploadChatAttachment> {
  if (attachment.type === "file") {
    const mimeType = attachment.file.type ? { mimeType: attachment.file.type } : {};
    return travelsAhead(attachment.file, uploadAhead)
      ? {
          type: "file",
          name: attachment.name,
          ...mimeType,
          sizeBytes: attachment.file.size,
          uploadedAttachmentId: await uploadFile(attachment.file),
        }
      : {
          type: "file",
          name: attachment.name,
          ...mimeType,
          sizeBytes: attachment.file.size,
          dataUrl: await readFileAsDataUrl(attachment.file),
        };
  }
  const folderEntries = await resolveBrowserFolderEntries(attachment);
  const entries: UploadChatFolderAttachmentEntry[] = [];
  for (const { relativePath, file } of folderEntries) {
    entries.push(
      travelsAhead(file, uploadAhead)
        ? { relativePath, sizeBytes: file.size, uploadedAttachmentId: await uploadFile(file) }
        : { relativePath, sizeBytes: file.size, dataUrl: await readFileAsDataUrl(file) },
    );
  }
  return {
    type: "folder",
    name: attachment.name,
    sizeBytes: entries.reduce((total, entry) => total + entry.sizeBytes, 0),
    entries,
  };
}

/**
 * What one send actually uploads for each composer attachment.
 *
 * A file or folder the user picked on the Environment host travels as its own
 * path: the App copies nothing and the agent works against the real thing.
 * Anything that only exists in the browser — a dropped `File`, a folder opened
 * through the File System Access API — has no host path to reference, so its
 * bytes travel and the Environment Service stores them. A file larger than the
 * inline limit is uploaded first through `uploadFile`, and the turn names only
 * the upload.
 */
export async function resolveComposerAttachmentsForSend(
  attachments: ReadonlyArray<ComposerPathAttachment>,
  options: {
    readonly uploadFile: ComposerAttachmentFileUploader;
    /** Upload every non-empty file ahead rather than only those over the inline limit. */
    readonly uploadAhead?: boolean;
  },
): Promise<ReadonlyArray<UploadChatAttachment>> {
  const uploads: UploadChatAttachment[] = [];
  for (const attachment of attachments) {
    if (attachment.source === "desktop" || attachment.source === "host-native") {
      const hostPath = attachment.source === "desktop" ? attachment.picked.path : attachment.path;
      uploads.push(
        attachment.type === "folder"
          ? { type: "folder", name: attachment.name, sizeBytes: 0, hostPath, entries: [] }
          : { type: "file", name: attachment.name, sizeBytes: 0, hostPath },
      );
      continue;
    }
    uploads.push(
      await uploadedBrowserAttachment(attachment, options.uploadFile, options.uploadAhead === true),
    );
  }
  return uploads;
}

interface BrowserFileSystemEntry {
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly name: string;
}

interface BrowserFileEntry extends BrowserFileSystemEntry {
  file(success: (file: File) => void, error?: (error: DOMException) => void): void;
}

interface BrowserDirectoryReader {
  readEntries(
    success: (entries: BrowserFileSystemEntry[]) => void,
    error?: (error: DOMException) => void,
  ): void;
}

interface BrowserDirectoryEntry extends BrowserFileSystemEntry {
  createReader(): BrowserDirectoryReader;
}

function readEntryFile(entry: BrowserFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function readDirectoryEntries(
  entry: BrowserDirectoryEntry,
): Promise<BrowserFileSystemEntry[]> {
  const reader = entry.createReader();
  const result: BrowserFileSystemEntry[] = [];
  while (true) {
    const batch = await new Promise<BrowserFileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) return result;
    result.push(...batch);
  }
}

async function collectDirectoryFiles(
  directory: BrowserDirectoryEntry,
  prefix = "",
): Promise<Array<{ readonly relativePath: string; readonly file: File }>> {
  const result: Array<{ readonly relativePath: string; readonly file: File }> = [];
  for (const entry of await readDirectoryEntries(directory)) {
    const relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.isFile) {
      result.push({ relativePath, file: await readEntryFile(entry as BrowserFileEntry) });
    } else if (entry.isDirectory) {
      result.push(...(await collectDirectoryFiles(entry as BrowserDirectoryEntry, relativePath)));
    }
  }
  return result;
}

export async function composerAttachmentsFromDrop(
  dataTransfer: DataTransfer,
  makeId: () => string,
): Promise<ComposerPathAttachment[]> {
  // A DataTransfer stops answering once the drop event returns, which is the
  // first await, so every item is read before any folder is walked.
  const fileItems = Array.from(dataTransfer.items).filter((item) => item.kind === "file");
  // Native drags are not consistent about exposing DataTransferItem entries:
  // an operating-system drag may populate `files` while leaving `items` empty.
  // Items carry folders, so they win; the FileList is the fallback.
  const dropped =
    fileItems.length > 0
      ? fileItems.map((item) => {
          const droppedItem = item as DataTransferItem & {
            webkitGetAsEntry?: () => BrowserFileSystemEntry | null;
            getAsFileSystemHandle?: () => Promise<FileSystemHandle | null>;
          };
          const entry = droppedItem.webkitGetAsEntry?.() ?? null;
          return {
            entry,
            file: item.getAsFile(),
            // Chromium hands over a live handle for a folder, which the composer
            // can stage like a picked one; it has to be asked during the event.
            handle: entry?.isDirectory
              ? (droppedItem.getAsFileSystemHandle?.().catch(() => null) ?? null)
              : null,
          };
        })
      : Array.from(dataTransfer.files).map((file) => ({ entry: null, file, handle: null }));

  const result: ComposerPathAttachment[] = [];
  for (const { entry, file, handle } of dropped) {
    if (entry?.isDirectory) {
      const id = makeId();
      const directoryHandle = await handle;
      result.push(
        directoryHandle?.kind === "directory"
          ? {
              id,
              source: "browser-folder",
              type: "folder",
              name: entry.name,
              entries: [],
              directoryHandle: directoryHandle as FileSystemDirectoryHandle,
            }
          : {
              id,
              source: "browser-folder",
              type: "folder",
              name: entry.name,
              entries: await collectDirectoryFiles(entry as unknown as BrowserDirectoryEntry),
            },
      );
    } else if (file) {
      result.push({ id: makeId(), source: "browser-file", type: "file", name: file.name, file });
    }
  }
  return result;
}

/** Build composer attachments from a browser `<input type="file">` selection. */
export function composerAttachmentsFromBrowserFiles(
  files: ReadonlyArray<File>,
  makeId: () => string,
): ComposerPathAttachment[] {
  const folderEntries = new Map<
    string,
    Array<{ readonly relativePath: string; readonly file: File }>
  >();
  const attachments: ComposerPathAttachment[] = [];
  for (const file of files) {
    const relativePath = file.webkitRelativePath?.replaceAll("\\", "/") ?? "";
    if (relativePath.includes("/")) {
      const root = relativePath.slice(0, relativePath.indexOf("/"));
      const nestedPath = relativePath.slice(root.length + 1);
      if (!nestedPath) continue;
      const entries = folderEntries.get(root) ?? [];
      entries.push({ relativePath: nestedPath, file });
      folderEntries.set(root, entries);
      continue;
    }
    attachments.push({
      id: makeId(),
      source: "browser-file",
      type: "file",
      name: file.name,
      file,
    });
  }
  for (const [name, entries] of folderEntries) {
    attachments.push({
      id: makeId(),
      source: "browser-folder",
      type: "folder",
      name,
      entries,
    });
  }
  return attachments;
}

/**
 * A file staged in the composer, as the send path's attachment type. Design and
 * media evidence can also be a host path or a folder, so both reach the send
 * through this one union rather than two parallel attachment channels.
 */
/**
 * What a staged file shows where a size goes.
 *
 * A picked host path is a reference, not a copy: there are no bytes to measure,
 * so reporting "0 B" would read as an empty file. It says what it is instead.
 */
export function composerFileSizeLabel(
  file: Pick<ComposerFileAttachment, "hostPath" | "isFolder" | "sizeBytes">,
): string {
  if (file.hostPath === undefined) return formatAttachmentSize(file.sizeBytes);
  return file.isFolder === true ? "Folder" : "On host";
}

export function composerFileAsPathAttachment(
  file: ComposerFileAttachment,
): ComposerPathAttachment | null {
  // A browser folder carries no path: its entries are read from the handle
  // when the turn is built, and a reload leaves nothing to read.
  if (file.isFolder === true && file.hostPath === undefined) {
    return file.folderHandle === undefined
      ? null
      : {
          id: file.id,
          source: "browser-folder",
          type: "folder",
          name: file.name,
          entries: [],
          directoryHandle: file.folderHandle,
        };
  }
  // A picked file or folder is a live reference to the Environment host, so it
  // travels as its path and never as bytes (ADR 0109).
  if (file.hostPath !== undefined) {
    return {
      id: file.id,
      source: "host-native",
      type: file.isFolder === true ? "folder" : "file",
      name: file.name,
      path: file.hostPath,
    };
  }
  // A hydrated draft can hold a file whose bytes did not survive the reload.
  // The composer shows those as needs-reattach and refuses to send them, so
  // there is nothing to carry here either.
  return file.file === null
    ? null
    : {
        id: file.id,
        source: "browser-file",
        type: "file",
        name: file.name,
        file: file.file,
      };
}

/**
 * A picked file or folder as the composer stages it.
 *
 * A host-path pick is a live reference: the Environment reads it from the path
 * (ADR 0109), so it carries no bytes and never uploads. A browser pick has
 * only bytes, so it stages like any dropped or pasted file. A browser folder
 * has neither shape the composer can hold — its entries are separate `File`s —
 * so it stays on the picker's side and is reported rather than staged.
 */
export function composerFileFromPathAttachment(
  attachment: ComposerPathAttachment,
): ComposerStagedPathAttachment | null {
  const hostPath = composerAttachmentHostPath(attachment);
  if (hostPath !== undefined) {
    return {
      type: "file",
      id: attachment.id,
      name: attachment.name,
      sizeBytes: 0,
      hostPath,
      file: null,
      ...(attachment.type === "folder" ? { isFolder: true as const } : {}),
    };
  }
  if (attachment.source === "browser-folder") {
    // Its entries are read from the handle when the turn is built, so there is
    // nothing to stage but the handle itself.
    return attachment.directoryHandle === undefined
      ? null
      : {
          type: "file",
          id: attachment.id,
          name: attachment.name,
          sizeBytes: 0,
          file: null,
          isFolder: true,
          folderHandle: attachment.directoryHandle,
        };
  }
  return attachment.source === "browser-file" ? { file: attachment.file } : null;
}

/** Either a staged composer file, or bytes the composer stages through its own file path. */
export type ComposerStagedPathAttachment =
  | {
      readonly type: "file";
      readonly id: string;
      readonly name: string;
      readonly sizeBytes: number;
      readonly hostPath: string;
      readonly file: null;
      readonly isFolder?: true;
    }
  | {
      readonly type: "file";
      readonly id: string;
      readonly name: string;
      readonly sizeBytes: 0;
      readonly file: null;
      readonly isFolder: true;
      readonly folderHandle: FileSystemDirectoryHandle;
    }
  | { readonly file: File };
